import { useEffect, useMemo, useState } from "react";
import { Search, X, Tag, Package, Wrench, Boxes } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Picker plein écran pour ajouter une ligne à un devis.
 * Sources : ouvrages (work_items.kind="ouvrage"), articles (work_items.kind="article"),
 * matériaux (/materials).
 *
 * Interactions :
 *   - Recherche live (code + description)
 *   - Filtre par catégorie (ouvrages/articles uniquement — matériaux ont leur propre filtre)
 *   - Onglets pour basculer entre les 3 sources
 *   - Double-clic OU bouton "Ajouter" sur une ligne → renvoie l'élément et ferme.
 *
 * onPick({ description, quantity, unit, unit_price_ht, code, work_item_id? })
 */
const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const WORK_CATS = [
  { value: "preparation", label: "Préparation" },
  { value: "peinture", label: "Peinture" },
  { value: "revetement_mural", label: "Revêtement mural" },
  { value: "revetement_sol", label: "Revêtement sol" },
  { value: "exterieur", label: "Extérieur / Façade" },
  { value: "autre", label: "Divers" },
  { value: "general", label: "Général" },
];

const MAT_CATS = [
  { value: "peinture", label: "Peinture" },
  { value: "enduit", label: "Enduit" },
  { value: "colle", label: "Colle" },
  { value: "toile_papier", label: "Toile / Papier" },
  { value: "fixation", label: "Fixation" },
  { value: "outillage_consommable", label: "Outillage / Consommable" },
  { value: "protection", label: "Protection" },
  { value: "diluant", label: "Diluant" },
  { value: "primaire", label: "Primaire" },
  { value: "vernis", label: "Vernis" },
  { value: "joint", label: "Joint" },
  { value: "divers", label: "Divers" },
];

const TABS = [
  { key: "ouvrage", label: "Main d'œuvre (Ouvrages)", icon: Wrench },
  { key: "article", label: "Articles", icon: Boxes },
  { key: "materiau", label: "Matériaux", icon: Package },
];

export default function QuoteItemPicker({ workItems, onPick, onClose, defaultTab = "ouvrage" }) {
  const [tab, setTab] = useState(defaultTab);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [materials, setMaterials] = useState([]);
  const [loadingMat, setLoadingMat] = useState(false);

  // Charge les matériaux à la demande (1 seule fois)
  useEffect(() => {
    if (tab === "materiau" && materials.length === 0 && !loadingMat) {
      setLoadingMat(true);
      api.get("/materials")
        .then((r) => setMaterials(r.data || []))
        .catch(() => setMaterials([]))
        .finally(() => setLoadingMat(false));
    }
  }, [tab, materials.length, loadingMat]);

  // Reset filtre catégorie quand on change de tab
  useEffect(() => {
    setCat("");
    setQ("");
  }, [tab]);

  // Fermeture clavier (Esc)
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (tab === "ouvrage" || tab === "article") {
      const wanted = tab; // "ouvrage" | "article"
      return (workItems || []).filter((w) => {
        if ((w.kind || "ouvrage") !== wanted) return false;
        if (cat && (w.category || "") !== cat) return false;
        if (!term) return true;
        const hay = `${w.code || ""} ${w.description || ""}`.toLowerCase();
        return hay.includes(term);
      });
    }
    // matériaux
    return materials.filter((m) => {
      if (cat && (m.category || "") !== cat) return false;
      if (!term) return true;
      const hay = `${m.name || ""} ${m.supplier || ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [tab, q, cat, workItems, materials]);

  const cats = tab === "materiau" ? MAT_CATS : WORK_CATS;

  const pick = (row) => {
    if (tab === "materiau") {
      onPick({
        material_id: row.id,
        description: row.name,
        code: "",
        unit: row.unit || "u",
        quantity: 1,
        unit_price_ht: Number(row.unit_price_ht) || 0,
      });
    } else {
      onPick({
        work_item_id: row.id,
        code: row.code,
        description: row.description,
        unit: row.unit || "u",
        quantity: 1,
        unit_price_ht: Number(row.sell_price_ht ?? row.suggested_price_ht ?? 0),
      });
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="quote-item-picker"
    >
      <div
        className="w-full max-w-5xl rounded-xl border shadow-2xl flex flex-col overflow-hidden"
        style={{ background: "var(--carbon-2)", borderColor: "var(--steel)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between border-b" style={{ borderColor: "var(--steel)" }}>
          <div>
            <h3 className="font-display text-lg sm:text-xl tracking-tight" style={{ color: "var(--chalk)" }}>
              Ajouter au devis
            </h3>
            <p className="text-xs mt-0.5" style={{ color: "var(--grey)" }}>
              Double-cliquez sur une ligne pour l&apos;ajouter au devis.
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            data-testid="picker-close"
            aria-label="Fermer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b" style={{ borderColor: "var(--steel)", background: "var(--carbon)" }}>
          {TABS.map(({ key, label, icon: Icon }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                onClick={() => setTab(key)}
                className="flex items-center gap-2 px-4 sm:px-5 py-3 text-[13px] font-medium transition-colors border-b-2"
                style={{
                  color: active ? "var(--pit-yellow)" : "var(--ink-soft)",
                  borderColor: active ? "var(--pit-yellow)" : "transparent",
                  background: active ? "rgba(255,196,0,0.06)" : "transparent",
                }}
                data-testid={`picker-tab-${key}`}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden">{key === "ouvrage" ? "MO" : key === "article" ? "Articles" : "Matériaux"}</span>
              </button>
            );
          })}
        </div>

        {/* Filtres */}
        <div className="px-5 py-3 flex flex-col sm:flex-row gap-2 border-b" style={{ borderColor: "var(--steel)" }}>
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--grey)" }} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher par code ou description…"
              autoFocus
              className="input w-full pl-9"
              data-testid="picker-search"
            />
          </div>
          <div className="relative">
            <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--grey)" }} />
            <select
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              className="input pl-9 pr-3 w-full sm:w-56"
              data-testid="picker-cat"
            >
              <option value="">Toutes catégories</option>
              {cats.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Liste résultats */}
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: "60vh" }}>
          {loadingMat && tab === "materiau" ? (
            <div className="p-10 text-center text-sm" style={{ color: "var(--grey)" }}>Chargement des matériaux…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm" style={{ color: "var(--grey)" }}>
              Aucun élément trouvé{q ? ` pour « ${q} »` : ""}.
            </div>
          ) : (
            <table className="table w-full">
              <thead className="sticky top-0" style={{ background: "var(--carbon)", zIndex: 1 }}>
                <tr>
                  <th className="w-24">Code</th>
                  <th>Description</th>
                  <th className="w-20">Unité</th>
                  <th className="w-28 text-right">Prix HT</th>
                  {tab === "ouvrage" && <th className="w-20 text-right">Temps</th>}
                  {tab === "article" && <th className="w-20 text-right">Stock</th>}
                  {tab === "materiau" && <th className="w-32">Fournisseur</th>}
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const isMat = tab === "materiau";
                  const isArt = tab === "article";
                  const isWi = tab === "ouvrage";
                  const price = isMat
                    ? row.unit_price_ht
                    : (row.sell_price_ht ?? row.suggested_price_ht ?? 0);
                  const minutes = isWi ? Math.round((Number(row.labor_hours_per_unit) || 0) * 60) : null;
                  return (
                    <tr
                      key={row.id}
                      onDoubleClick={() => pick(row)}
                      className="cursor-pointer hover:bg-[rgba(255,196,0,0.06)] transition-colors"
                      data-testid={`picker-row-${row.id}`}
                    >
                      <td className="font-mono text-xs font-bold">{isMat ? "—" : (row.code || "—")}</td>
                      <td className="text-sm">
                        <div>{isMat ? row.name : row.description}</div>
                        {isMat && row.category && (
                          <div className="text-[11px] mt-0.5" style={{ color: "var(--grey)" }}>{row.category}</div>
                        )}
                      </td>
                      <td className="text-sm">{row.unit || "u"}</td>
                      <td className="text-right font-mono tabular-nums">{fmtEUR(price)}</td>
                      {isWi && (
                        <td className="text-right font-mono text-xs" style={{ color: "var(--grey)" }}>
                          {minutes > 0 ? `${minutes} min` : "—"}
                        </td>
                      )}
                      {isArt && (
                        <td className="text-right font-mono text-xs">
                          {Number(row.stock_qty || 0)} {row.unit || ""}
                        </td>
                      )}
                      {isMat && (
                        <td className="text-xs" style={{ color: "var(--grey)" }}>{row.supplier || "—"}</td>
                      )}
                      <td>
                        <button
                          onClick={(e) => { e.stopPropagation(); pick(row); }}
                          className="btn btn-sm btn-accent"
                          data-testid={`picker-add-${row.id}`}
                        >
                          Ajouter
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-5 py-3 border-t text-xs flex items-center justify-between gap-3"
          style={{ borderColor: "var(--steel)", color: "var(--grey)", background: "var(--carbon)" }}
        >
          <span>
            <b className="font-mono" style={{ color: "var(--chalk)" }}>{filtered.length}</b> résultat
            {filtered.length > 1 ? "s" : ""}
          </span>
          <span className="hidden sm:inline">
            💡 Astuce : double-clic = ajout rapide · <kbd className="px-1.5 py-0.5 rounded bg-[var(--carbon-2)] border border-[var(--steel)] font-mono text-[10px]">Esc</kbd> pour fermer
          </span>
        </div>
      </div>
    </div>
  );
}
